/**
 * Centralized operator-facing copy for shared Admin v2 shells and high-traffic
 * routes. Keeping these strings in one place makes future localization or copy
 * audits possible without re-scanning every route component.
 */
export const adminUiCopy = {
  common: {
    retry: "Retry",
    retryLoading: "Retry loading",
    refresh: "Refresh",
    close: "Close",
    openApprovalsQueue: "Open approvals queue",
    openFeatureFlags: "Open feature flags",
    openEntitlements: "Open entitlements",
    openDashboard: "Open dashboard",
    requestAccess: "Request access",
    signOut: "Sign out",
    copyUserId: "Copy user ID",
    errorCodeLabel: "Error code",
    correlationIdLabel: "Correlation ID",
    statusActive: "Active",
    statusInactive: "Inactive",
    statusUnavailable: "Status unavailable",
    notAvailable: "Not available",
    notSet: "Not set",
    helpAriaPrefix: "Help",
    moreInformation: "More information",
  },
  loading: {
    generic: "Loading…",
    adminRouteTitle: "Loading admin page",
    adminRouteSubtitle: "The next admin page is still loading.",
    adminWorkspaceTitle: "Loading admin workspace",
    adminWorkspaceSubtitle:
      "Checking your sign-in state before opening the admin workspace.",
    adminWorkspaceStatus: "Preparing the admin workspace.",
    signInStatus:
      "Checking your sign-in details before opening the admin workspace.",
    adminAccess: "Loading admin access…",
    adminAccessCheck: "Checking admin access…",
  },
  adminLoadFailure: {
    defaultHelp:
      "Retry this view. If it still fails after a refresh, capture the affected filters before escalating.",
    recordHelp:
      "If it still fails after a refresh, include the correlation ID when you report it.",
    chunkLoadTitle: "This admin page needs a refresh",
    chunkLoadMessage:
      "Part of this admin page did not load. This can happen just after a deploy or when cached files do not match the latest build.",
    chunkLoadHelp:
      "Reload admin to fetch the latest files. If this still fails, share the reference below with the team handling the preview site.",
    reloadAdmin: "Reload admin",
  },
  authAccess: {
    deniedTitle: "This account does not have admin access",
    deniedSubtitle:
      "This account can sign in, but it cannot open the admin workspace.",
    deniedHelp:
      "Ask a platform administrator to grant admin access. Include the user ID below.",
    signedInAsTitle: "Signed in as",
    nameLabel: "Name",
    emailLabel: "Email",
    signOutHint:
      "Not your usual admin account? Sign out and use the correct admin account instead.",
    userIdLabel: "User ID (Firebase UID)",
    claimCheckTimedOutTitle: "Admin access check timed out",
    claimCheckTimedOutMessage:
      "We could not confirm your admin access right now. Protected admin pages stay blocked until the check succeeds.",
    claimCheckFailedTitle: "Admin access could not be confirmed",
    claimCheckFailedMessage:
      "We could not verify your admin access right now. Protected admin pages stay blocked until the check succeeds.",
    modalTitle: "More access is required",
    modalNotice: "You need more access to complete this action.",
    modalHelp:
      "Ask a platform administrator to review your role access, then request approval for this area if needed.",
    accessNeededTitle: "Access needed for this area",
    noCurrentRoleAccess:
      "Your current role does not include the access needed for this action.",
    // D-T42: a signed-in-as session refused by SPOOF_ACCESS_FORBIDDEN; more role access would not help.
    signedInAsSessionCannotOpen:
      "This signed-in-as session can't open this item. Sign in with your own Admin account to see it.",
    // OD-27: the title for that refusal; asking for more access would not help.
    signedInAsModalTitle: "Not available in a signed-in-as session",
    approvalCreateFailed:
      "We could not create the access request. Retry or open the approvals queue.",
    approvalsQueueAccessHelp:
      "Ask a platform administrator with Approvals access to review your role. Approvals stay closed until they update your access.",
  },
  authLogin: {
    headerEyebrow: "Secure staff sign-in",
    cardEyebrow: "Secure sign-in",
    title: "Sign in to admin",
    subtitle: "Use an approved staff account to open the admin workspace.",
    emailLabel: "Email address *",
    passwordLabel: "Password *",
    emailButton: "Sign in with email",
    emailButtonBusy: "Signing in...",
    providerDivider: "Or use",
    googleButton: "Sign in with Google",
    xeroButton: "Sign in with Xero",
    quickBooksButton: "Sign in with QuickBooks",
    buildLabelPrefix: "Admin build",
    tokenFailure:
      "We couldn't restore this admin sign-in. Please tell the devs to relogin to Codex, then try again.",
    signedOutInAnotherTab:
      "You signed out in another tab. Unsaved changes here weren't saved.",
    authSessionLost:
      "Your admin sign-in expired or did not load correctly. Please tell the devs to relogin to Codex, then try again.",
    genericFailure: "Sign-in failed",
    popupBlocked:
      "Your browser blocked the sign-in window. Allow pop-ups for this site, then try again.",
    popupClosed:
      "The sign-in window closed before it finished. Allow pop-ups for this site, keep this tab in front while you sign in, then try again.",
    storageUnavailable:
      "Your browser is blocking the storage this sign-in needs. Turn off private browsing or allow site data for this site, then try again.",
  },
  authForgotPassword: {
    pageTitle: "Reset password",
    title: "Reset your password",
    subtitle: "Enter the email address for your admin account.",
    emailLabel: "Email address",
    emailPlaceholder: "name@example.com",
    submit: "Continue",
    invalidEmail: "Enter a valid email address",
  },
  apiHealthGate: {
    checkingTitle: "Checking admin services",
    checkingStatus: "Checking",
    checkingDescription:
      "We are confirming the backend is available before loading the admin dashboard.",
    systemHealth: "System status",
    routeShellTitle: "Admin shell",
    routeShellStatus: "Waiting",
    routeShellDescription:
      "Holding the dashboard shell until the health check finishes.",
    routeShellLiveStatus: "Waiting for the admin API to respond.",
    retryConnection: "Retry connection",
    retryChecking: "Checking…",
    down: {
      service: {
        title: "Admin data service unavailable",
        message:
          "Navigation worked, but this page cannot load because the Admin data service did not respond.",
        openMessage:
          "The Admin data service is not responding. This page stays open, but saving may fail until the connection returns.",
        connectionLabel: "Admin data service",
        connectionStatus: "Not connected",
        recovery: "Wait a moment, then retry the connection.",
      },
      localhost: {
        title: "Local Admin backend is not running",
        message:
          "Navigation worked, but this page cannot load because the Admin frontend is running and the local backend is not responding.",
        openMessage:
          "The local Admin backend is not responding. This page stays open, but saving may fail until it is running again.",
        connectionLabel: "Local backend",
        connectionStatus: "Not connected",
        recovery: "Start the local backend, then retry the connection.",
      },
      offline: {
        title: "You are offline",
        message:
          "The Admin page opened, but it cannot connect because this browser is offline.",
        openMessage:
          "You are offline. This page stays open, but saving may fail until you reconnect.",
        connectionLabel: "Internet connection",
        connectionStatus: "Offline",
        recovery: "Reconnect to the internet, then retry the connection.",
      },
    },
    degradedBanner:
      "Admin services are degraded. You can keep using the admin app, but some actions may still fail until the backend recovers.",
  },
  routeAccess: {
    title: "Access check failed",
    deniedTitle: "Access unavailable",
    deniedMessage:
      "You no longer have access to this area. Ask an administrator to check your account.",
    subtitle:
      "Admin could not confirm access to this area from the backend right now.",
    timedOutMessage:
      "Access checks timed out. Retry the checks or review approvals.",
    unavailableMessage:
      "We could not verify access right now. Retry the checks or review approvals.",
    supportHelp:
      "If this keeps happening, contact support and share the correlation ID.",
    retryChecks: "Retry access check",
    loadingTitle: "Checking access",
    loadingMessage: "We are checking whether this account can open this area.",
    loadingStatus: "Checking access…",
    permissionDeniedTitle: "More access is required",
    permissionDeniedSubtitle:
      "This area exists in admin, but your current role does not include the permission needed to open it.",
    permissionDeniedMessage:
      "This account does not have permission to open this area.",
    visibilityDeniedTitle: "This workspace is unavailable",
    visibilityDeniedSubtitle:
      "This workspace exists in admin, but it is currently hidden for this account by access visibility rules.",
    visibilityReason:
      "This block comes from workspace visibility controls for the current scope, not from your role permissions.",
    visibilityHelpManage:
      "Review the feature flags and entitlements keeping this workspace hidden, then retry.",
    visibilityHelpRequest:
      "Ask a platform administrator to review the feature flags and entitlements keeping this workspace hidden.",
    accessSummaryTitle: "Your account and access",
    accessSummaryCopy:
      "Open to review the signed-in account, roles, permissions, and visible workspaces.",
  },
  routeUnavailable: {
    title: "Page not found",
    subtitle: "This admin page does not exist at this URL.",
    help: "Check the URL, or open Dashboard to keep working.",
    signedInAccountLabel: "Signed-in account",
  },
  shellAccess: {
    degradedBanner:
      "Some admin access data could not be loaded. Navigation is running in limited mode while we reconnect, so some destinations may stay unavailable.",
  },
  shellChrome: {
    adminNavigation: "Admin navigation",
    searchAdmin: "Search admin or jump to a record",
    searchAccessLoading: "Search access is loading",
    searchRequiresMoreAccess: "Search requires more access",
    searchPages: "Search pages",
    searchAdminPages: "Search admin pages",
    signedInAs: "Signed in as",
    adminOperator: "Admin operator",
    userIdLabel: "User ID",
    dashboard: "Dashboard",
    adminOnly: "Admin only",
    allSections: "All sections",
    closeNavigationMenu: "Close navigation menu",
    openNavigationMenu: "Open navigation menu",
    collapseSidebar: "Collapse sidebar",
    expandSidebar: "Expand sidebar",
    openUtilityActions: "Open utility actions",
    openGlobalSearch: "Open global search",
    openUserMenu: "Open user menu",
    openLiveAlertsFeed: "Open Live Alerts Feed",
    liveAlertsFeed: "Live Alerts Feed",
    issueReports: "Issue reports",
    featureRequests: "Feature requests",
    moreUtilities: "More utilities",
    openSpoofCustomerSession: "Open View as customer",
    spoofCustomer: "View as customer",
    logInAsUserSpoofSession: "View as customer",
    spoofSessionAudit: "Spoof session audit",
    legacyAppBugTracker: "Legacy app bug tracker",
    showUnavailablePages: "Show unavailable pages",
    currentPage: "Current page",
    pinCurrentPage: "Pin current page",
    unpinCurrentPage: "Unpin current page",
    pinnedPages: "Pinned",
    recentPages: "Recent",
    pinnedDestinations: "Pinned destinations",
    recentDestinations: "Recent destinations",
    noMatchingPages: "No pages match this search.",
    signOutLabel: "Sign out",
    buildLabelPrefix: "Admin build",
    unknownAdmin: "Unknown admin",
    emailUnavailable: "Email not available",
    userIdUnavailable: "User ID not available",
    reportAppBug: "Report app bug",
    requestFeature: "Request a feature",
    transcriptIntake: "Transcript intake",
    helpAndSupport: "Help & Support",
  },
  systemLogs: {
    title: "System logs",
    subtitle:
      "Read-only diagnostic view for backend log entries when the log source is available.",
    retryLoading: "Retry loading logs",
    sourceUnavailable:
      "The backend log feed is not connected in this environment, so this route cannot show recent backend entries right now. Use backend request captures for server-side checks, or download this browser session's logs on localhost.",
    sourceUnavailableAction: "Download browser session logs",
    sourceUnavailableActionVisible: "Download session logs",
    sourceUnavailableDownloadStarted: "Browser session log download started.",
    sourceUnavailableNoSessionLogs:
      "No browser session logs are ready yet. Reproduce the issue on this page, then download again.",
    sourceUnavailableDownloadFailed:
      "Browser session logs could not be downloaded. Use backend request captures for this check.",
    sourceUnavailableEmptyTable:
      "The backend log feed is unavailable in this environment.",
    organisationScopeNotice: "Showing backend entries for organisation",
    organisationScopeClear: "Show all organisations",
    organisationScopeEmpty:
      "No matching backend entries in the last three days at info level or above.",
    sourceUnavailableFilteredEmptyTable:
      "The backend log feed is unavailable, so there are no entries to search.",
    loadingEntries: "Loading log entries…",
    loadFailureMessage: "Failed to load system logs.",
    metadataTitle: "Metadata summary",
    metadataSummaryHelp:
      "This summary shortens long values and redacts text that looks sensitive. Open raw metadata only when you need more detail.",
    noMetadataSummary: "No metadata was recorded for this log entry.",
    showRawMetadata: "Show raw metadata",
    hideRawMetadata: "Hide raw metadata",
    rawMetadataTruncated:
      "This raw view is shortened to keep the dialog usable.",
  },
  routeGateSummary: {
    signedInAccount: "Signed-in account",
    nameLabel: "Name",
    emailLabel: "Email",
    userIdLabel: "User ID",
    statusLabel: "Status",
    rolesTitle: "Roles",
    accessTitle: "Access",
    permissionsLabel: "Permissions",
    visibleWorkspacesLabel: "Visible workspaces",
    visibleWorkspacesScopedEmpty: "None enabled for this visibility scope",
    hiddenWorkspaceLabel: "Hidden workspace",
    visibilityScopeLabel: "Visibility scope",
    visibilityChecksLabel: "Checks keeping this hidden",
    requestedRouteLabel: "Requested route",
    noneLoadedYet: "None loaded yet",
    noneEnabled: "None enabled",
    bootstrapAdmin: "Bootstrap admin",
    e2eBypassRole: "Admin claim accepted in end-to-end test mode",
    noStaffRolesAssigned: "No staff roles assigned",
    workspaceVisibilityRevenue: "Revenue",
    workspaceVisibilityLearning: "Learning",
    workspaceVisibilitySales: "Sales",
    e2eBypassNotice: "This check is running in end-to-end test bypass mode.",
  },
  ownerRuntime: {
    statuses: {
      requested: {
        label: "Requested",
        help: "The action was received and is waiting for the next check.",
      },
      validation_failed: {
        label: "Needs correction",
        help: "The action could not start because required details were missing or did not pass checks. Review the request and correct it before trying again.",
      },
      awaiting_review: {
        label: "Awaiting review",
        help: "A reviewer needs to approve this action before it can continue.",
      },
      approved: {
        label: "Approved",
        help: "Review is complete and the action is ready to move into the work queue.",
      },
      queued: {
        label: "Queued",
        help: "The action is lined up and will run when capacity is available.",
      },
      applying: {
        label: "Applying",
        help: "The action is running now. Wait for the next status update before retrying anything.",
      },
      failed_retryable: {
        label: "Retry needed",
        help: "The action stopped before finishing, but it may succeed on another attempt. Check the latest activity and retry when the blocking issue is clear.",
      },
      failed_terminal: {
        label: "Stopped",
        help: "The action cannot continue in its current state. Review the request details, capture the reference, and decide whether a new request is needed.",
      },
      succeeded: {
        label: "Completed",
        help: "The action finished successfully.",
      },
      rejected: {
        label: "Rejected",
        help: "A reviewer declined this action. Check the review notes before making another request.",
      },
      cancelled: {
        label: "Cancelled",
        help: "This action was stopped before completion and will not continue unless a new request is created.",
      },
      expired: {
        label: "Expired",
        help: "This action took too long to continue. Start a new request if the work is still needed.",
      },
    },
    unavailable: {
      blocked: {
        title: "Action blocked",
        help: "This action cannot continue yet. Review the latest status and complete any required next step before trying again.",
      },
      permission_denied: {
        title: "More access is required",
        help: "Your current admin access does not allow this action. Ask a platform administrator to review your access for this area.",
      },
      missing_context: {
        title: "More details are required",
        help: "This action is missing required context. Confirm the correct account, record, and request details before trying again.",
      },
      review_required: {
        title: "Review required",
        help: "This action stays blocked until a reviewer approves it. Open the approvals queue or ask the assigned reviewer to take the next step.",
      },
      owner_unavailable: {
        title: "Assigned owner unavailable",
        help: "The action cannot continue because the responsible owner is not available right now. Reassign it or hand it over through the normal support path.",
      },
      unsafe_state: {
        title: "Action unavailable right now",
        help: "The current state is not safe for this action. Refresh the latest activity, confirm the record is ready, and try again only after the blocking issue is resolved.",
      },
      unsupported_action: {
        title: "Action not available here",
        help: "This action is not supported for the current record or state. Use another supported workflow instead of retrying here.",
      },
      fallback: {
        title: "Action unavailable",
        help: "This action is unavailable right now. Refresh the page, review the latest status, and escalate with the reference if the block remains.",
      },
    },
  },
  errorStates: {
    genericLoadTitle: "We couldn't load this right now",
  },
  asyncState: {
    retrySection: "Retry loading this section",
    retryData: "Retry loading this data",
    approvalPermissionHelp:
      "If you need approval follow-up from here, ask an admin who manages approvals to review this area.",
  },
  crmContacts: {
    title: "All contacts",
    createContact: "New contact",
    exportBlocked: "Export blocked",
    loadFailureTitle: "Contacts unavailable",
    loadFailureMessage: "We could not load the contacts workspace right now.",
  },
  csvExport: {
    menuButton: "Export CSV",
    approvalRequired: "Create an approval request before retrying this export.",
    approvalRequestFailed:
      "We couldn't create the approval request. Retry, or open the approvals queue to review pending requests.",
    confirmTitle: "Confirm CSV export",
    usersInExportPrefix: "Users in this export",
    includedFieldsTitle: "Included fields",
    downloadCsv: "Download CSV",
    requestApproval: "Request approval",
    retryExport: "Retry export",
    approvalFlowHelp:
      "Request approval creates the approval request first. Retry export runs the export again after approval is available.",
    approvalWarningPrefix:
      "This export needs approval because it includes a large set of sensitive user data.",
    currentRequestPrefix: "Current request",
    requestTypePrefix: "Request type",
    includedFieldsSummary:
      "The CSV includes name, email, subscription status, activity status, login count, last login, signup date, organisation details, email verified status, phone number, and Stripe customer link.",
  },
  previewEmailGate: {
    warningText:
      "Non-production sends are blocked unless this is set. Use a test address that can receive all test emails.",
    invalidEmail: "Enter a valid email address for preview delivery.",
    resetToDefault: "Reset to default override",
    clearStored: "Clear stored email",
    saveOverride: "Save override",
    title: "Set preview email override",
    recipientLabel: "Test recipient email",
    requiredNotice:
      "The override is required before sending any non-production email.",
    defaultOverridePrefix: "Default override:",
  },
  supportRoutes: {
    issueReports: "Issue reports",
    issueReport: "Admin site report",
    logInAsUser: "View as customer",
    choosePreviewBuild: "Advanced customer view",
    accessSessionAudit: "Customer-view audit",
    startSupportSession: "Create customer view",
    viewSessionTimeline: "Open customer-view audit",
    testDataReset: "Test data reset",
    previewKeepList: "Preview keep-list",
  },
  previewKeepList: {
    pageTitle: "Preview keep-list",
    pageSubtitle:
      "Preview only. The preview database is replaced by a copy of production every morning at 5:30am. Organisations listed here are carried over automatically so they survive that refresh.",
    unavailableMessage: "Only available on the preview stack.",
    introNoticeTitle: "How the keep-list works",
    introNoticeBody:
      "Every morning at 5:30am, preview is replaced by a fresh copy of production, and any preview-only organisation, membership, trial or test data disappears. Add an organisation below to snapshot it automatically and carry it over the next refresh. Provider connections are not carried over. Reconnect Xero or QuickBooks after a restore.",
    statusCardTitle: "Snapshot and restore status",
    storageErrorWarning:
      "The snapshot store could not be read, so snapshots and restores will fail until it is fixed. Check the preview API's bucket access.",
    currentRefreshLabel: "Current preview build",
    currentRefreshValue: "Refresh {operationId}",
    currentRefreshUnknown: "Not detected on this database",
    lastSnapshotLabel: "Last snapshot",
    lastSnapshotValue:
      "{takenAt} · {organisationCount} organisations · {rowCount} rows · {trigger}",
    lastSnapshotNone: "No snapshot has been taken yet",
    lastRestoreLabel: "Last restore",
    lastRestoreValue:
      "{restoredAt} · {organisationCount} organisations · {insertedRows} rows added · {purgedRows} rows replaced · {trigger}",
    lastRestoreNone: "No restore has run yet",
    pendingRestoreWarning:
      "Preview was refreshed since the last snapshot and the kept organisations have not been carried over yet. This happens automatically at quarter past each hour, or press Restore now.",
    restoreFailedWarning:
      "The last carry-over failed at {failedAt} and nothing was changed. It will try again at quarter past the hour. Error {errorCode}{detail}.",
    restoreFailedDetail: " on {table} ({constraint})",
    restoreFailedDetailNoConstraint: " on {table}",
    snapshotAction: "Snapshot now",
    restoreAction: "Restore now",
    restoreDialogTitle: "Restore the preview keep-list",
    restoreDialogDescription:
      "Brings back every kept organisation from the last snapshot, replacing any preview-only rows for the same organisations that arrived with this morning's refresh. Provider connections are not carried over.",
    restoreConfirmationCheckboxLabel:
      "I understand this replaces any current preview data for these organisations with the last snapshot",
    restoreConfirmAction: "Confirm restore",
    entriesTableTitle: "Kept organisations",
    entriesTableEmptyState:
      "No organisations are kept yet. Add one below so it survives the next preview refresh.",
    entriesTableOrganisationColumn: "Organisation",
    entriesTableProviderColumn: "Provider",
    entriesTableLabelColumn: "Label",
    entriesTableReasonColumn: "Reason",
    entriesTableAddedByColumn: "Added by",
    entriesTableAddedAtColumn: "Added",
    entriesTableRemoveAction: "Remove",
    addFormTitle: "Add an organisation to the keep-list",
    addFormOrganisationIdLabel: "Organisation ID",
    addFormOrganisationIdHint:
      "The organisation's UUID, from its Admin page URL.",
    addFormOrganisationIdInvalid: "Enter a valid organisation ID (a UUID).",
    addFormLabelLabel: "Label",
    addFormLabelPlaceholder: "e.g. Demo Company (NZ)",
    addFormReasonLabel: "Reason",
    addFormReasonPlaceholder: "e.g. Used for the onboarding demo script",
    addFormSubmitAction: "Add to keep-list",
    addFormSuccess: "Organisation added to the keep-list.",
    outcomeSnapshotted: "Snapshot taken.",
    outcomeSnapshotEmpty: "Nothing to snapshot yet. Add an organisation first.",
    outcomeSnapshotPendingRestore:
      "Preview was refreshed since the last snapshot. Restore first, then snapshot again.",
    outcomeSnapshotUnavailable:
      "Snapshots are only available on the preview stack.",
    outcomeRestored: "Restore complete.",
    outcomeRestoreNoSnapshot: "There is no snapshot to restore yet.",
    outcomeRestoreAlreadyApplied:
      "This snapshot has already been carried over to the current preview build.",
    outcomeRestoreNotRefreshed:
      "Preview has not been refreshed since the last snapshot, so there is nothing to carry over.",
    outcomeRestoreLocked:
      "Another restore is already running. Try again in a moment.",
    outcomeRestoreUnavailable:
      "Restores are only available on the preview stack.",
    errorFallback: "Something went wrong. Try again or ask engineering.",
  },
  testReset: {
    pageTitle: "Test data reset",
    pageSubtitle:
      "Staging only. Permanently deletes an organisation's test data so onboarding can be retested with the same email and company.",
    unavailableTitle: "Not available here",
    unavailableMessage: "Only available on the preview/staging stack.",
    emailLabel: "Email",
    emailPlaceholder: "name@example.com",
    searchAction: "Search",
    noUsersFound: "No sign-in user found for that email.",
    noOrganisationsForUser:
      "This sign-in has no organisations attached, so there is nothing to reset. It is already at the start of onboarding: the next sign-in with this email continues from the connect step. A reset earlier today would leave the account in exactly this state.",
    otherMembersWarning:
      "Other people belong to this organisation and will lose it too",
    resetAction: "Reset test data",
    confirmationCheckboxLabel:
      "I understand this permanently deletes all data for this organisation on staging",
    deleteUserRowCheckboxLabel: "Also delete the sign-in user row",
    clearFhcFreeReportCheckboxLabel:
      "Also let this organisation generate another free Financial Health Check",
    clearFhcFreeReportHint:
      "A reset on its own leaves the one free report per organisation guard in place, because the record of the first report deliberately outlives the organisation. Tick this only to retest the funnel.",
  },
  growthTags: {
    pageTitle: "Audiences",
    pageSubtitle:
      "Manage the tags Audiences can use in filters, CRM handoffs, and downstream owner actions. Preview contact changes before you request them.",
    listTitle: "Tags",
    listLoading: "Loading tags…",
    emptyState:
      "No tags matched the current filters. Clear filters or create a tag for the selected organisation.",
    formCreateTitle: "Create tag",
    formEditTitle: "Edit tag",
    organisationHint:
      "Audiences tags are scoped to one organisation. Archive a tag when it should stop being available for new work in that organisation.",
    governanceTitle: "Tag filters",
    governanceSubtitle:
      "Tags are labels. Segments are saved filters promoted for reusable audience use.",
    governanceNotice:
      "Audiences tags are organisation-scoped. Archived tags stay visible on records but cannot be used for new rules or handoffs. Bulk apply and remove changes require a preview before you request them.",
    bulkTitle: "Tagging Panel",
    bulkSubtitle:
      "Preview contact changes before you request the change, so you can catch missed selected contacts or no-op requests.",
    bulkEmptyState:
      "Select an Audiences tag to preview apply or remove changes.",
    loadFailureTitle: "Tags unavailable",
    loadFailureMessage: "We could not load tags for the current filters.",
    retryFailedMessage:
      "Loaded failed contacts back into the editor. Preview again before retrying.",
    archiveAction: "Archive Audiences tag",
    archiveDialogTitle: "Archive selected Audiences tag",
    archiveDialogBody: "Archive {name} ({slug})?",
    archiveDialogHelp:
      "Archived tags stay visible on records where already applied, but should not be selected for new audience rules or owner-surface handoffs.",
    archiveConfirmAction: "Confirm archive",
    archivedSuccess: "Audiences tag archived.",
    archivedFailure:
      "Audiences tag was not archived. Check the tag, then retry.",
  },
  growthSegments: {
    loadFailureTitle: "Audiences unavailable",
    loadFailureMessage:
      "We could not load saved audiences for the current filters. Retry, or clear filters and create an audience for the selected organisation.",
  },
  revenueSafety: {
    checking:
      "Checking Stripe safety rails for this environment. Revenue mutations stay blocked until test mode is confirmed.",
    safe: "Stripe test mode confirmed. Revenue tools are running against non-production Stripe.",
    mismatch:
      "Stripe safety mismatch detected. Resolve the API/config mode mismatch before running revenue mutations.",
    liveMode:
      "Stripe live mode detected. Revenue mutations stay blocked in this non-production environment.",
    unavailable:
      "Stripe safety could not be confirmed. Treat billing mutations as unavailable until the environment check succeeds.",
    manualCheckoutBlocked:
      "Manual checkout links stay disabled until Stripe safety confirms test mode.",
  },
  manualCheckout: {
    title: "Create manual checkout link",
    subtitle:
      "Create a one-time Stripe checkout link for one organisation and one user. Links expire 24 hours after creation.",
    safetyWarning:
      "This creates a real Stripe checkout link and can affect customer billing. Use it only with explicit approval, and do not share links in public channels.",
    acknowledgement:
      "I have verified the organisation, user, and product are correct",
    productLoading: "Loading products from Stripe…",
    productReady: "Select the product to include in the manual checkout link.",
    ticketReferencePlaceholder: "e.g. monday-9576806482",
    ticketReferenceHelp:
      "Use 3–80 letters, numbers, hyphens, or underscores. Do not enter a URL or customer details.",
    ticketReferenceInvalid:
      "Enter 3–80 characters using only letters, numbers, hyphens, or underscores.",
    confirmLabel: "Type GENERATE to enable",
    confirmHelp:
      "Type GENERATE exactly to confirm that you want to create the manual checkout link.",
    createAction: "Create manual checkout link",
    createActionBusy: "Generating manual checkout link…",
    resetAction: "Reset form",
    createdNotice: "Manual checkout link created. Share it within 24 hours.",
    copiedNotice: "Copied manual checkout link to clipboard",
    copyFailedNotice: "Failed to copy manual checkout link",
    copyLink: "Copy manual checkout link",
    sharePrompt:
      "Share this promptly. Stripe checkout links expire after 24 hours.",
  },
  hoverHelp: {
    systemLogs: {
      fetch:
        "Controls how many log rows to load for this view before filtering. Larger values can take longer to load.",
      severity:
        "Filters visible logs by severity after the window is loaded. Start with errors and warnings when you are investigating a problem.",
      searchMessage:
        "Searches only the message text in the currently loaded log window.",
    },
    revenueBilling: {
      organisationScope:
        "Leave this blank to include all organisations for this view. Enter one organisation UUID to narrow the page to a single organisation.",
      timeRange:
        "These presets define the date window for the summary and list data. Year to date starts on January 1 in UTC.",
      refresh:
        "Reloads data for the current scope and filters. Use this after changing filters or when you need fresh webhook activity.",
      transactionStatus:
        "Filter transactions by their billing status, such as succeeded, failed, or refunded.",
      subscriptionStatus:
        "Filter subscriptions by their current Stripe lifecycle state, such as active, trialing, or canceled.",
      disputeStatus:
        "Filter disputes by their current response state so you can focus on open or resolved cases.",
      webhookStatus:
        "Show only webhook events in one processing state at a time, such as failed or queued.",
      activeSubscriptionsSummary:
        "Counts active subscriptions in up to 100 records loaded for the current scope and time range.",
      purchasesLastThirtyDays:
        "Counts succeeded purchases in up to 100 transactions loaded for the last 30 days, regardless of the selected summary time range.",
      disputesSummary:
        "Counts disputes in up to 100 records loaded for the current organisation scope and summary time range.",
      refundApprovalRequestId:
        "Use this when refunds must be tied to an approval request before finance or support can complete them.",
      refundAmount:
        "Leave this blank for a full refund, or enter a smaller amount in cents for a partial refund.",
      refundReason:
        "Choose a Stripe refund reason only when Stripe reporting needs it. Internal case context belongs in the note field below.",
      refundInternalNote:
        "Explain why this refund is needed so approvers and audit logs can match it to the support case.",
      executeRefund:
        "Runs the approved refund against Stripe for the selected transaction. Check the approval request, amount, and reason first.",
      activeSubscriptionsContext:
        "Up to 100 loaded records for the current scope and time range.",
      purchasesLastThirtyDaysContext:
        "Up to 100 loaded succeeded purchases across the current scope.",
      disputesContext:
        "Up to 100 loaded records for the current scope and time range.",
    },
    teamPermissions: {
      refreshStaffList:
        "Reloads the latest staff, role, and permission data for this view.",
      searchStaffUsers:
        "Searches the current staff list by name, email, or user ID.",
      permissionKey:
        "Permission keys are internal access labels. Search by the first word, such as support, billing, or crm, to find the right one.",
      overrideAction:
        "Grant gives this user that one permission. Deny removes that one permission, even if a role would normally allow it.",
      inheritedPermissions:
        "This list shows your own raw access keys. It does not show the selected team member's access.",
      filterEffectivePermissions:
        "Searches only your own access keys shown below.",
    },
    emailSender: {
      refreshTemplates:
        "Reloads the saved template list from admin storage in case another operator has updated templates recently.",
    },
    bulkEmailJobs: {
      status:
        "Status filtering runs on the server first, so this page only loads jobs in the selected state.",
      search:
        "Search narrows the jobs already loaded into this page by job ID, subject, template, status, or creator.",
    },
    docsShare: {
      searchScope:
        "Search matches the doc path only. Use archived docs when you need old scratchpads or retired notes.",
      expiresAt: "After this time, the link is blocked automatically.",
      visibility:
        "Status shows whether the current link is active, expired, or revoked.",
      singleUse:
        "Access count shows how many times the link has already been opened.",
      archivedDocs:
        "Archived docs include older scratchpads and retired notes that are not part of the current working set.",
      createNewLink:
        "Creates a new share link for this doc path using the current expiry setting.",
      disableLink:
        "Revoked links stop working immediately, even if someone still has the old URL.",
    },
    organisations: {
      search:
        "Searches the organisation name or organisation ID using the current filters.",
      subscription:
        "Limits the list to organisations in the selected subscription state.",
      exportVisible:
        "Exports only the organisations currently visible after filters are applied.",
      provider:
        "Provider shows which upstream source or integration currently owns this organisation record.",
      sort: "Sort changes the order of the current results only. It does not change which organisations are included.",
    },
    settingsScope: {
      organisationScope:
        "Leave this blank to use the default scope. Enter one organisation UUID to narrow the page to that organisation.",
      includeGlobal:
        "Includes global records alongside organisation-scoped records when an organisation scope is set.",
      refresh:
        "Reloads data for the current scope without changing the filters.",
    },
    csvExport: {
      requestApproval:
        "Creates the approval request needed before this export can continue.",
      retryExport:
        "Runs the export again after approval has been created or approved.",
    },
    previewEmail: {
      testRecipient:
        "Overrides where non-production preview emails are delivered for this browser session.",
      resetOverride:
        "Clears the saved override so the default preview-email destination is used again.",
    },
    subscriptionDashboard: {
      openStripeDashboard:
        "Opens the related Stripe dashboard in a new tab for provider-side review.",
      manualAdjustments:
        "Manual support actions update admin-side billing records and support tools. They do not replace Stripe-side changes when Stripe is the source of truth.",
      manualCheckoutOrganisation:
        "Choose the organisation that will own the checkout session and receive the billing record.",
      manualCheckoutUser:
        "Choose the user who should be linked to this checkout session for audit and ownership.",
      manualCheckoutProduct:
        "Select the active catalog product that should be used for this checkout link.",
      manualCheckoutPrice:
        "Choose the exact Stripe price for this product when more than one price is available.",
      manualCheckoutTicketReference:
        "Add the support ticket, case, or approval reference used to justify this billable action.",
      manualCheckoutConfirm:
        "Typing GENERATE confirms that you want to create a real checkout session for the selected organisation, user, and product.",
      manualCheckoutCreate:
        "Creates a real Stripe checkout session. Record the case reference before sharing the link.",
    },
    featureFlags: {
      organisationScope:
        "Leave this blank to review or save global flags. Enter one organisation UUID only when you mean to scope the flag to that organisation.",
      includeGlobal:
        "When organisation scope is set, this also shows global flags so you can compare global and organisation-specific rollout.",
      rolloutPercentage:
        "This is a percentage, not a user count. Use 100 for full rollout and lower values for staged release.",
      saveFeatureFlag:
        "Save writes the current key, scope, enabled state, rollout, and metadata exactly as shown here.",
    },
    messagingTemplates: {
      statusFilter:
        "Filter by draft, published, or archived status so you do not edit the wrong template state.",
      channelFilter:
        "Choose the message channel first because email and in-app templates can use different content and variables.",
      categoryFilter:
        "Use category to separate transactional messages from marketing content before editing or reviewing.",
      deleteTemplate:
        "Deleting removes this saved template from the shared library. Archived or copied versions are not restored automatically.",
      refreshAction: "Refresh templates",
      sendTestAction: "Send test email",
      sendTestVisibleNotice:
        "Test delivery goes only to @hellocashflow.com staff inboxes or a server-approved API-test address. Customer addresses are never used.",
      deleteImpact:
        "This removes the template from the shared library for all admins and cannot be undone.",
    },
    coupons: {
      organisationId:
        "Use one organisation UUID to narrow the view or save the coupon against one organisation.",
      status:
        "Status shows whether the coupon can be used now, is inactive, or has been archived for history only.",
      duration:
        "Once applies one time, repeating applies for a set number of billing cycles, and forever continues until the subscription ends or the coupon is removed.",
      discountType:
        "Percent discounts reduce the price by a percentage. Amount discounts reduce it by a fixed currency amount.",
      discountValue:
        "Percent values show a percentage. Amount values show the stored minor-unit amount with its currency.",
      percentOff:
        "Enter a percentage greater than zero. Use this when the discount should scale with the checkout amount.",
      amountOffCents:
        "Enter the fixed discount in the currency's minor units, such as cents for USD.",
      durationInMonths:
        "Use this only for repeating coupons. Leave it blank for once or forever coupons.",
      syncStripe:
        "When this is turned on, the coupon is also created or updated in Stripe as part of the save.",
      metadataJson:
        "Enter valid JSON only. Use metadata for structured support or billing context that needs to travel with the coupon.",
    },
    affiliates: {
      selfReferralsBlocked:
        "Blocks commissions when the affiliate is also the buyer or is tied to the same tracked account.",
      manualReviewRequired:
        "Holds attribution or payout work for manual review before it can move forward.",
      attributionRule:
        "Controls which affiliate gets credit when more than one referral touch is recorded.",
      attributionWindowDays:
        "Sets how many days after the qualifying referral touch a conversion can still be credited.",
      clawbackPolicy:
        "Defines whether refunded or disputed sales should reverse previously earned affiliate payouts.",
      allowedOfferIds:
        "List the offer IDs this affiliate can earn commission from. Leave blank to use the default eligibility rules.",
      outstandingPayouts:
        "Shows payout amounts still waiting to be recorded or completed for this affiliate in the current scope.",
    },
    revenueBillingTerms: {
      paymentIntent:
        "The Stripe payment intent groups the payment attempt from confirmation through final status.",
      charge:
        "The Stripe charge is the captured payment record created when money is actually taken or attempted.",
      mappedEntity:
        "This is the internal billing entity linked to the Stripe subscription, such as the organisation or another tracked billing record.",
      stripeCustomer:
        "The Stripe customer record that owns the billing profile for this subscription.",
    },
  },
} as const;

export type AdminUiCopy = typeof adminUiCopy;
